import { format, parseISO } from 'date-fns'
import { useState } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import useSentEmail from '@/lib/api/med-requests/sent-email'

function SentEmailDialog({ logId, onClose }: { logId: string | null; onClose: () => void }) {
  const { data, isLoading, isError } = useSentEmail(logId ?? undefined, !!logId)

  return (
    <Dialog open={!!logId} onOpenChange={open => !open && onClose()}>
      <DialogContent className='max-w-3xl'>
        <DialogTitle>Email sent to patient</DialogTitle>
        {isLoading ? (
          <p className='text-custom-neutral-600 text-sm leading-[170%] py-8 text-center'>Loading email...</p>
        ) : isError || (!data?.html && !data?.presignedUrl) ? (
          <p className='text-custom-neutral-600 text-sm leading-[170%] py-8 text-center'>
            Could not load the sent email.
          </p>
        ) : (
          <iframe
            title='Sent email'
            sandbox=''
            {...(data.presignedUrl ? { src: data.presignedUrl } : { srcDoc: data.html })}
            className='w-full h-[70vh] border-0 rounded-lg bg-white'
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function parseText(text: string) {
  const urlRegex = /(https:\/\/workdrive\.zohoexternal\.com\/external\S+)/g

  const linkifyLine = (line: string, lineIndex: number) => {
    const parts = line.split(urlRegex)
    return parts.map((part, i) => {
      if (part.startsWith('https://workdrive.zohoexternal.com/external')) {
        return (
          <a
            key={`${lineIndex}-${i}`}
            href={part}
            target='_blank'
            rel='noopener noreferrer'
            className='text-blue-600 hover:underline'
          >
            {part}
          </a>
        )
      }
      return <Fragment key={`${lineIndex}-${i}`}>{part}</Fragment>
    })
  }

  return text
    .split('\n')
    .flatMap((line, i) => (i === 0 ? [linkifyLine(line, i)] : [<br key={`br-${i}`} />, linkifyLine(line, i)]))
}

function Cell({ text }: { text: string }) {
  return (
    <div className='w-2/5 whitespace-pre-wrap break-words h-[120px] overflow-y-auto'>
      <p className='text-custom-neutral-800 text-sm leading-[170%]'>{parseText(text)}</p>
    </div>
  )
}

function FallbackColumn({ header, text }: { header: string; text: string }) {
  return (
    <div className='w-2/5 whitespace-pre-wrap break-words h-[120px] overflow-y-auto'>
      <p className='text-[#1D1D1D] text-sm font-bold leading-[170%] mb-2'>{header}</p>
      <p className='text-custom-neutral-800 text-sm leading-[170%]'>{parseText(text)}</p>
    </div>
  )
}

function formatEntryDate(date: string) {
  try {
    return format(parseISO(date), 'dd MMM yyyy, HH:mm')
  } catch {
    return date
  }
}

export default function OnHoldHistory() {
  const medRequest = useCurrentMedication()
  const data = medRequest?.data
  const [showEarlier, setShowEarlier] = useState(false)
  const [viewedEmailLogId, setViewedEmailLogId] = useState<string | null>(null)

  if (!data) return null

  const historyEntries = Array.isArray(data.onHoldHistory) ? data.onHoldHistory : null
  const hasArray = !!historyEntries && historyEntries.length > 0

  const sortedEntries = hasArray
    ? [...historyEntries!].sort((a, b) => {
        const aTime = Date.parse(a.date)
        const bTime = Date.parse(b.date)
        if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0
        return bTime - aTime
      })
    : []

  const visibleEntries = showEarlier ? sortedEntries : sortedEntries.slice(0, 1)
  const earlierCount = sortedEntries.length - 1

  return (
    <div id='on-hold-history'>
      <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%] mb-6'>On hold history</p>
      {hasArray ? (
        <div className='flex flex-col w-full p-8 rounded-3xl border border-custom-neutral-200 bg-[#FAFAFB]'>
          <div className='flex w-full justify-between items-center gap-4 pb-3'>
            <p className='w-2/5 text-[#1D1D1D] text-sm font-bold leading-[170%]'>On Hold reason</p>
            <div className='w-px' />
            <p className='w-2/5 text-[#1D1D1D] text-sm font-bold leading-[170%]'>Patient response</p>
          </div>
          {visibleEntries.map((entry, idx) => (
            <Fragment key={`${entry.date}-${idx}`}>
              <div className='h-px w-full bg-custom-neutral-200' />
              <div className='flex flex-col w-full py-3 gap-2'>
                <div className='flex items-center gap-4'>
                  <p className='text-custom-neutral-600 text-xs leading-[170%]'>{formatEntryDate(entry.date)}</p>
                  {entry.emailLogId && (
                    <button
                      type='button'
                      onClick={() => setViewedEmailLogId(entry.emailLogId!)}
                      className='text-xs font-semibold leading-[170%] text-[#153CB7] hover:underline'
                    >
                      View email sent
                    </button>
                  )}
                </div>
                <div className='flex w-full justify-between items-start gap-4'>
                  <Cell text={entry.reason || '-'} />
                  <div className='self-stretch w-px bg-custom-neutral-200' />
                  <Cell text={entry.patientResponse || '-'} />
                </div>
              </div>
            </Fragment>
          ))}
          {earlierCount > 0 && (
            <>
              <div className='h-px w-full bg-custom-neutral-200' />
              <button
                type='button'
                onClick={() => setShowEarlier(prev => !prev)}
                className='self-start mt-3 text-sm font-semibold leading-[170%] text-[#153CB7] hover:underline'
              >
                {showEarlier ? 'Hide earlier holds' : `Show ${earlierCount} earlier hold${earlierCount > 1 ? 's' : ''}`}
              </button>
            </>
          )}
        </div>
      ) : (
        <div className='flex h-[216px] w-full p-8 justify-between items-center gap-4 rounded-3xl border border-custom-neutral-200 bg-[#FAFAFB]'>
          <FallbackColumn header='On Hold reason' text={data.reasonForHold ?? '-'} />
          <div className='h-full w-px bg-custom-neutral-200' />
          <FallbackColumn header='Patient response' text={data.patientsResponseForHold ?? '-'} />
        </div>
      )}
      <SentEmailDialog logId={viewedEmailLogId} onClose={() => setViewedEmailLogId(null)} />
    </div>
  )
}
